/**
 * The Share button's whole payload (MATT GO 2026-10-25 01:06; docs/specs/share-button.md): the site's address and one
 * fixed sentence, nothing else: no results, amounts, years, names or tracking parameters. This file is in the start
 * chunk because a phone's share sheet must be opened inside the tap itself; the panel and the QR code are a lazy chunk
 * (./panel.ts). Nothing here makes a request.
 */
import site from './site.json';

/** The address that is shared, copied and put in the QR code. */
export const SHARE_URL: string = site.url;

export interface SharePayload {
  text: string;
  url: string;
}

/** What leaves the device when someone shares: the sentence in the app's language and the address. */
export const sharePayload = (tr: (key: string) => string): SharePayload => ({
  // The app wraps an English fallback in direction marks inside a right-to-left page; a message to someone else has none.
  text: tr('share.message').replace(/[\u2066-\u2069]/g, ''),
  url: SHARE_URL,
});

/** The parts of the browser that decide how sharing goes (replaceable in tests). */
export interface ShareEnv {
  /** A touch phone or tablet (coarse pointer): the native share sheet is the right control there. */
  coarse: boolean;
  share?: ((data: SharePayload) => Promise<void>) | undefined;
  canShare?: ((data: SharePayload) => boolean) | undefined;
}

export const browserShareEnv = (): ShareEnv => ({
  coarse: typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
  share: typeof navigator.share === 'function' ? (d) => navigator.share(d) : undefined,
  canShare: typeof navigator.canShare === 'function' ? (d) => navigator.canShare(d) : undefined,
});

/**
 * A tap on a share button. On a phone with a share sheet: the sheet opens at once (no await before it, the browser
 * wants the tap's permission); cancelling it does nothing, any other failure opens the panel. Everywhere else: the panel.
 */
export function startShare(tr: (key: string) => string, openPanel: () => void, env: ShareEnv = browserShareEnv()): void {
  const payload = sharePayload(tr);
  if (env.coarse && env.share && (!env.canShare || env.canShare(payload))) {
    let started: Promise<void>;
    try {
      started = env.share(payload);
    } catch {
      openPanel();
      return;
    }
    started.catch((e: unknown) => {
      if ((e as { name?: string } | null)?.name !== 'AbortError') openPanel();
    });
    return;
  }
  openPanel();
}
